import axios, {AxiosError, AxiosInstance} from 'axios';
import {EventEmitter} from 'events';

import {AuthService} from '@/services/AuthService';
import {IApiResponse, IUserData} from '@/models/ApiModels';
import {i18n} from "@/main";

const TOKEN_INVALID = "Token invalid";

const createAxiosInstance = (endpoint: string) => axios.create({
  baseURL: endpoint,
  headers: {
    'Content-Type': 'application/json',
  },
});


enum SEND_TYPE {
  PATCH,
  POST,
  PUT,
  GET,
}

export class ApiCaller {
  private axios: AxiosInstance;
  private tokenPromise: Promise<any>;

  constructor(
      private authService: AuthService,
      private endpoint: string,
      private logBus: EventEmitter,
  ) {
    this.axios = createAxiosInstance(endpoint);
    this.axios.defaults.headers.common.Authorization = '';
    this.tokenPromise = Promise.resolve(); // by default its an already resolved promise, so we can do the requests
    // But when a request gives a wrong token, we should do the suitable operations (go back to login, or maybe
    // remember-me login)

    this.authService.authToken$$
        .subscribe(
            (token: string) => {
              this.updateToken(token);
            },
        );
  }

  public testOldToken(): Promise<boolean>  {
    const token = this.authService.getOldToken();
    this.updateToken(token);

    return this.GET("/user")
      .then((result: IUserData) => {
        this.authService.nextToken(token); // todo: test if this works when it is a constructor parameter.
        return true;
      })
      .catch((err) => {
        return false;
      });
  }

  public GET(urlPath: string, defVal: any = null): Promise<any> {
    // todo: security system to not to make a get call if we are not authorized?
    if (this.axios.defaults.headers.common.Authorization === '') {
      return Promise.resolve(defVal);
    }

    const info = 'apicall.' + urlPath;
    this.logBus.emit('COMM', 'START: ' + info);

    const doApiCall = () => this.mapResult(this.AxiosGET(urlPath), info);

    return doApiCall()
      .catch((err: any) => {
        if (defVal === null) {
          throw err;
        } else {
          return defVal;
        }
      });
  }


  public PATCH(urlPath: string, data: any = null): Promise<any> {
    return this.SEND(SEND_TYPE.PATCH, urlPath, data);
  }


  public POST(urlPath: string, data: any = null): Promise<any> {
    return this.SEND(SEND_TYPE.POST, urlPath, data);
  }


  public PUT(urlPath: string, data: any = null): Promise<any> {
    return this.SEND(SEND_TYPE.PUT, urlPath, data);
  }

  /**
   * updates the header with the token
   * @param token
   * @private
   */
  private updateToken(token: string) {
    this.axios.defaults.headers.common.Authorization = 'Bearer ' + token;
  }

  /**
   * POST without any kind of postProcess.
   * @param urlPath
   * @param data
   * @constructor
   */
  private AxiosPOST(urlPath: string, data: any = null): Promise<any> {
    return this.axios.post(urlPath, data);
  }

  /**
   * PATCH without any kind of postProcess.
   * @param urlPath
   * @param data
   * @constructor
   */
  private AxiosPATCH(urlPath: string, data: any = null): Promise<any> {
    return this.axios.patch(urlPath, data);
  }


  /**
   * PUT without any kind of postProcess.
   * @param urlPath
   * @param data
   * @constructor
   */
  private AxiosPUT(urlPath: string, data: any = null): Promise<any> {
    return this.axios.put(urlPath, data);
  }

  /**
   * GET without any kind of postProcess.
   * @param urlPath
   * @constructor
   */
  private AxiosGET(urlPath: string): Promise<any> {
    return this.axios.get(urlPath);
  }


  // will do mapping axios result to inner data only AND error handling
  private async mapResult(result$: Promise<IApiResponse<any>>, info: string) {

    return result$
        .then((result: IApiResponse<any>) => {
          this.logBus.emit('COMM', '200OK:' + info);

          const token = result.data.token;
          if (token) {
            this.authService.nextToken(token);
          }

          return result.data.data;
        })
        .catch((err: AxiosError) => this.axiosErrorHandler(err, info));
  }


  private axiosErrorHandler(err: AxiosError, info: string) {
    this.logBus.emit('COMM', 'ERROR:' + info, err);

    if (getErrorStatus(err) === 401) {
      this.logBus.emit('ERROR', 'token expired and no remember me');
      this.authService.clearToken();
      return Promise.reject(err);
    }

    throw err;
  }


  private SEND(sendType: SEND_TYPE, urlPath: string, data: any = null): Promise<any> {
    const sendResult = sendType === SEND_TYPE.PATCH ? this.AxiosPATCH(urlPath, data)
      : sendType === SEND_TYPE.POST ? this.AxiosPOST(urlPath, data)
        : sendType === SEND_TYPE.PUT ? this.AxiosPUT(urlPath, data)
          : this.AxiosPOST(urlPath, data);


    const info = 'apicall.' + urlPath;
    this.logBus.emit('COMM', 'START: ' + info);
    const doApiCall = () => this.mapResult(sendResult, info);

    return doApiCall()
      .catch((err: any) => {
        // Re-throw to catch in component
        throw err;
      });
  }

}

const getErrorStatus = (err: AxiosError) => {
  // (err.response || {}).status
  return err.response && err.response.status
    ? err.response.status
    : null;
};

